import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import {
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  ImageList,
  ImageListItem,
  IconButton as MuiIconButton,
  Card,
  Chip,
  CardMedia,
  CardContent,
  CardActions,
  Checkbox,
  ListItemText
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { collection, getDocs, deleteDoc, doc, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase/config';

function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    category: '',
    stock: '',
    images: [],
    sizes: [],
    colors: [],
    featured: false,
    currentColor: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sizes, setSizes] = useState(['Standart', 'XS', 'S', 'M', 'L', 'XL', 'XXL']);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (editingProduct && editingProduct.sizes) {
      setSelectedSizes(editingProduct.sizes);
    } else {
      setSelectedSizes([]);
    }
  }, [editingProduct]);

  const fetchProducts = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'products'));
      const productsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('Yüklenen ürünler:', productsData);
      setProducts(productsData);
    } catch (error) {
      setError('Ürünler yüklenirken hata oluştu');
      console.error('Ürünler yüklenirken hata:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'categories'));
      const categoriesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCategories(categoriesData);
    } catch (error) {
      setError('Kategoriler yüklenirken hata oluştu');
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    try {
      setLoading(true);
      setError('');

      const uploadPromises = files.map(async (file) => {
        const extension = file.name.split('.').pop();
        const fileName = `product-images/${Date.now()}-${Math.random().toString(36).substring(7)}.${extension}`;
        const storageRef = ref(storage, fileName);
        
        await uploadBytes(storageRef, file);
        return getDownloadURL(storageRef);
      });

      const uploadedUrls = await Promise.all(uploadPromises);

      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls]
      }));

      setSuccess('Resimler başarıyla yüklendi');
    } catch (error) {
      setError('Resimler yüklenirken bir hata oluştu');
      console.error('Yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        title: product.title,
        description: product.description,
        price: product.price,
        category: product.category,
        images: product.images || [],
        stock: product.stock,
        sizes: product.sizes || [],
        colors: product.colors || [],
        featured: product.featured,
        currentColor: '',
      });
    } else {
      setEditingProduct(null);
      setFormData({
        title: '',
        description: '',
        price: '',
        category: '',
        images: [],
        stock: '',
        sizes: [],
        colors: [],
        featured: false,
        currentColor: '',
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingProduct(null);
    setFormData({
      title: '',
      description: '',
      price: '',
      category: '',
      images: [],
      stock: '',
      sizes: [],
      colors: [],
      featured: false,
      currentColor: '',
    });
    setSelectedSizes([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const productData = {
        ...formData,
        sizes: selectedSizes,
        price: Number(formData.price),
        stock: Number(formData.stock),
        updatedAt: serverTimestamp()
      };

      if (editingProduct) {
        await updateDoc(doc(db, 'products', editingProduct.id), productData);
        setSuccess('Ürün başarıyla güncellendi');
      } else {
        await addDoc(collection(db, 'products'), {
          ...productData,
          createdAt: serverTimestamp()
        });
        setSuccess('Ürün başarıyla eklendi');
      }

      handleClose();
      fetchProducts();
    } catch (error) {
      setError('İşlem sırasında bir hata oluştu');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu ürünü silmek istediğinizden emin misiniz?')) {
      try {
        await deleteDoc(doc(db, 'products', id));
        setSuccess('Ürün başarıyla silindi');
        fetchProducts();
      } catch (error) {
        setError('Ürün silinirken bir hata oluştu');
      }
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleColorAdd = () => {
    const color = formData.currentColor.trim();
    if (color && !formData.colors.includes(color)) {
      setFormData(prev => ({
        ...prev,
        colors: [...prev.colors, color],
        currentColor: ''
      }));
    }
  };

  const handleColorDelete = (colorToDelete) => {
    setFormData(prev => ({
      ...prev,
      colors: prev.colors.filter(color => color !== colorToDelete)
    }));
  };

  const renderProductImage = (images) => {
    if (!images || images.length === 0) {
      return '/placeholder-image.jpg'; // Varsayılan bir resim yolu ekleyin
    }
    return images[0];
  };

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" component="h1">
            Ürün Yönetimi
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingProduct(null);
              setFormData({
                title: '',
                description: '',
                price: '',
                category: '',
                stock: '',
                images: [],
                sizes: [],
                colors: [],
                featured: false,
                currentColor: '',
              });
              setOpen(true);
            }}
          >
            Yeni Ürün Ekle
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Grid container spacing={3}>
          {products.map((product) => (
            <Grid item xs={12} sm={6} md={4} key={product.id}>
              <Card>
                <CardMedia
                  component="img"
                  height="200"
                  image={renderProductImage(product.images)}
                  alt={product.title}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent>
                  <Typography variant="h6" noWrap>
                    {product.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {product.description}
                  </Typography>
                  <Typography variant="subtitle1" color="primary">
                    {product.price?.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Stok: {product.stock || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Kategori: {product.category || 'Belirtilmemiş'}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpen(product)}
                  >
                    Düzenle
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDelete(product.id)}
                  >
                    Sil
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingProduct ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Ürün Adı"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  sx={{ mb: 2 }}
                />

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Kategori</InputLabel>
                  <Select
                    value={formData.category}
                    label="Kategori"
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.slug}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  type="number"
                  label="Fiyat"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  type="number"
                  label="Stok"
                  value={formData.stock}
                  onChange={(e) => setFormData({ ...formData, stock: Number(e.target.value) })}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Açıklama"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  sx={{ mb: 2 }}
                />

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="sizes-label">Boyutlar</InputLabel>
                  <Select
                    labelId="sizes-label"
                    multiple
                    value={selectedSizes}
                    onChange={(e) => setSelectedSizes(e.target.value)}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 250
                        },
                      },
                    }}
                  >
                    {sizes.map((size) => (
                      <MenuItem key={size} value={size}>
                        <Checkbox checked={selectedSizes.indexOf(size) > -1} />
                        <ListItemText primary={size} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Renkler
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    {formData.colors?.map((color, index) => (
                      <Chip
                        key={index}
                        label={color}
                        onDelete={() => handleColorDelete(color)}
                        size="small"
                      />
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      label="Yeni Renk"
                      value={formData.currentColor}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        currentColor: e.target.value
                      }))}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleColorAdd}
                      disabled={!formData.currentColor.trim()}
                      size="small"
                    >
                      Ekle
                    </Button>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Ürün Görselleri
                </Typography>
                <ImageList sx={{ width: '100%', height: 200 }} cols={4} rowHeight={164}>
                  {formData.images.map((image, index) => (
                    <ImageListItem key={index}>
                      <img
                        src={image}
                        alt={`Ürün görseli ${index + 1}`}
                        loading="lazy"
                        style={{ objectFit: 'cover' }}
                      />
                      <IconButton
                        sx={{
                          position: 'absolute',
                          right: 4,
                          top: 4,
                          bgcolor: 'background.paper'
                        }}
                        size="small"
                        onClick={() => removeImage(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ImageListItem>
                  ))}
                </ImageList>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  Görsel Ekle
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                  />
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>İptal</Button>
            <Button onClick={handleSubmit} variant="contained">
              {editingProduct ? 'Güncelle' : 'Ekle'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
}

export default AdminProducts; 